import { useEffect } from "react";
import { Link } from "@tanstack/react-router";
import { Calendar, MapPin, Phone, Store, X, Star } from "lucide-react";
import { salons } from "@/data/salons";
import { track } from "@/lib/analytics";
import { getSalonCtas } from "@/lib/cta";
import type { Salon } from "@/data/types";
import { SalonRatingLines } from "@/components/SalonRatingLines";
import { internalPath } from "@/lib/paths";

export type PickerAction = "select" | "directions" | "call" | "book";

const ACTION_COPY: Record<PickerAction, { title: string; sub: string; icon: typeof Store }> = {
  select: { title: "Välj salong", sub: "Fyra salonger i Stockholm", icon: Store },
  directions: { title: "Vägbeskrivning", sub: "Välj vilken salong du vill ha väg till", icon: MapPin },
  call: { title: "Ring salong", sub: "Välj vilken salong du vill ringa", icon: Phone },
  book: { title: "Boka tid", sub: "Välj salong för att boka i BokaDirekt", icon: Calendar },
};

interface Props {
  open: boolean;
  action: PickerAction;
  onClose: () => void;
}

export function SalonPickerSheet({ open, action, onClose }: Props) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;
  const meta = ACTION_COPY[action];
  const Icon = meta.icon;

  return (
    <div className="fixed inset-0 z-[80] md:items-center md:justify-center md:flex" role="dialog" aria-modal="true" aria-label={meta.title}>
      <button
        type="button"
        aria-label="Stäng"
        onClick={onClose}
        className="absolute inset-0 bg-ink/55 backdrop-blur-sm"
      />
      <div className="absolute inset-x-0 bottom-0 md:relative md:max-w-lg md:w-full md:rounded-2xl rounded-t-3xl bg-background shadow-lift max-h-[85vh] flex flex-col">
        <div className="flex items-start justify-between gap-4 p-5 border-b border-border">
          <div>
            <div className="inline-flex items-center gap-2 text-brass-deep">
              <Icon size={16} />
              <span className="eyebrow">{meta.title}</span>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">{meta.sub}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Stäng" className="p-2 -m-2 text-foreground/60">
            <X size={20} />
          </button>
        </div>
        <ul className="overflow-y-auto p-2 pb-[max(env(safe-area-inset-bottom),1rem)]">
          {salons.map((s) => (
            <li key={s.slug}>
              <SalonRow salon={s} action={action} onClose={onClose} />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function SalonRow({ salon, action, onClose }: { salon: Salon; action: PickerAction; onClose: () => void }) {
  const baseClass =
    "w-full flex items-center justify-between gap-3 p-4 rounded-xl hover:bg-secondary/60 active:bg-secondary text-left";
  const meta = (
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <span className="font-medium">{salon.shortName}</span>
        {salon.featuredLabel && (
          <span className="text-[10px] tracking-wider uppercase bg-brass/15 text-brass-deep px-1.5 py-0.5 rounded">
            {salon.featuredLabel}
          </span>
        )}
      </div>
      <div className="mt-0.5 text-xs text-muted-foreground flex items-start gap-2">
        <Star size={11} className="fill-brass text-brass" />
        <SalonRatingLines salon={salon.slug} />
      </div>
      <div className="mt-0.5 text-xs text-muted-foreground truncate">
        {salon.address}, {salon.city}
      </div>
    </div>
  );

  const handle = (location: string) =>
    track(
      action === "book"
        ? "book_now_click"
        : action === "call"
          ? "call_click"
          : action === "directions"
            ? "directions_click"
            : "salon_select",
      { salon_name: salon.shortName, cta_location: location },
    );

  if (action === "select") {
    return (
      <Link
        to={internalPath(salon.url)}
        className={baseClass}
        onClick={() => {
          handle("picker_select");
          onClose();
        }}
      >
        {meta}
        <span className="text-brass-deep text-sm shrink-0">Se salong →</span>
      </Link>
    );
  }
  const ctas = getSalonCtas(salon);
  if (action === "call") {
    return (
      <a
        href={ctas.call.href}
        className={baseClass}
        onClick={() => {
          handle("picker_call");
          onClose();
        }}
      >
        {meta}
        <span className="text-brass-deep text-sm shrink-0">{salon.phoneDisplay}</span>
      </a>
    );
  }
  if (action === "directions") {
    return (
      <a
        href={ctas.directions.href}
        target="_blank"
        rel="noopener noreferrer"
        className={baseClass}
        onClick={() => {
          handle("picker_directions");
          onClose();
        }}
      >
        {meta}
        <span className="text-brass-deep text-sm shrink-0">Karta →</span>
      </a>
    );
  }
  return (
    <a
      href={ctas.book.href}
      target="_blank"
      rel="noopener noreferrer"
      className={baseClass}
      onClick={() => {
        handle("picker_book");
        onClose();
      }}
    >
      {meta}
      <span className="text-brass-deep text-sm shrink-0">Boka →</span>
    </a>
  );
}
